import React, { useEffect, useMemo, useRef, useState } from "react";
import { audioSupported } from "../../lib/keyboardPlan";
import { playMidiSeq, playMidiChord, fmtMs, SESSION_HEARTS } from "../../lib/numberSenseV2";
import {
  Icon,
  Piano,
  PlayButton,
  TypingHint,
  MidiBadge,
  useTypingPiano,
} from "../numberSenseUi";
import useMidi from "../numberSense/useMidi";

const CAN_PLAY = audioSupported();

/* ============================================================
   Number Sense v2 — the drill runtime. One Duolingo-style session
   (~10 generated questions) for a chosen scale type + key: pattern
   questions, note↔degree translation, chord qualities, an ear
   check, and a tap-the-scale finale on the piano.

   Much smaller than v1's LessonScreen because every question is
   either multiple-choice or the one build type — the variety lives
   in the generators (numberSenseV2.js), not the renderer.

   onFinish(passed, { hearts, correct, total, results, totalMs })
   — results carries per-question { typeId, keyName, correct, ms }
   for mastery, drill records, and the answer streak.

   `timed` (only true once this drill has enough perfect runs)
   shows a live run clock; time is measured either way, counting
   only question-active time, so reading a verdict costs nothing.
   `startStreak` seeds the cross-run "N in a row" display.
   ============================================================ */

// "hear the scale" for ear/build questions — schedules raw midis, so flat
// keys don't get re-voiced the way pitch-class playback would.
const MidiPlayButton = ({ midis, label = "hear it", big = false }) => {
  const [playing, setPlaying] = useState(false);
  const run = () => {
    const ms = playMidiSeq(midis, 300);
    setPlaying(true);
    window.setTimeout(() => setPlaying(false), ms);
  };
  return (
    <button
      type="button"
      className={`ns-play ${big ? "big" : ""} ${playing ? "on" : ""}`}
      onClick={run}
    >
      <Icon name="play" />
      <span>{label}</span>
    </button>
  );
};

const QKIND = {
  step: "the step pattern",
  fullpattern: "the step pattern",
  scalenote: "name the note",
  degree: "name the degree",
  nextnote: "keep climbing",
  quality: "the chord it builds",
  ear: "trust your ears",
  chordear: "chord ears",
  build: "build the scale",
  buildinv: "build the flip",
  invname: "name the flip",
  invear: "flip ears",
};

// Inversions play exact midis in sequence — one or more full chords, never
// re-voiced — so which note sits on the bottom survives playback.
const MidiChordButton = ({ chords, label = "hear it", big = false }) => {
  const [playing, setPlaying] = useState(false);
  const run = () => {
    chords.forEach((midis, i) => playMidiChord(midis, 700, i * 0.9));
    setPlaying(true);
    window.setTimeout(() => setPlaying(false), chords.length * 900);
  };
  return (
    <button
      type="button"
      className={`ns-play ${big ? "big" : ""} ${playing ? "on" : ""}`}
      onClick={run}
    >
      <Icon name="play" />
      <span>{label}</span>
    </button>
  );
};

const DrillScreen = ({
  session,
  timed = false,
  startStreak = 0,
  onClose,
  onFinish,
}) => {
  const { questions } = session;
  const [qi, setQi] = useState(0);
  const [hearts, setHearts] = useState(SESSION_HEARTS);
  const [correct, setCorrect] = useState(0);
  const [selection, setSelection] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [lastCorrect, setLastCorrect] = useState(false);
  const [built, setBuilt] = useState(() => new Set());
  const [builtKeys, setBuiltKeys] = useState(() => new Map()); // buildinv: id → midi
  const [results] = useState(() => []);
  const [streakNow, setStreakNow] = useState(startStreak);

  // Question-active time only: the clock for a question starts when it shows
  // and stops at Check, so reading verdicts never costs the run.
  const qStart = useRef(Date.now());
  const spentMs = useRef(0);
  const answeredRef = useRef(false);
  answeredRef.current = answered;
  const [clock, setClock] = useState(0);
  useEffect(() => {
    if (!timed) return undefined;
    const t = window.setInterval(() => {
      setClock(
        spentMs.current + (answeredRef.current ? 0 : Date.now() - qStart.current)
      );
    }, 500);
    return () => window.clearInterval(t);
  }, [timed]);

  const q = questions[qi];
  const total = questions.length;
  const pct = Math.round((qi / total) * 100);
  const practice = Boolean(session.practice); // no hearts — every run finishes

  const grade = (ok) => {
    if (answered) return;
    const ms = Date.now() - qStart.current;
    spentMs.current += ms;
    results.push({ typeId: q.typeId, keyName: q.keyName, correct: ok, ms });
    setAnswered(true);
    setLastCorrect(ok);
    setStreakNow(ok ? streakNow + 1 : 0);
    const nextCorrect = correct + (ok ? 1 : 0);
    const nextHearts = ok || practice ? hearts : hearts - 1;
    setCorrect(nextCorrect);
    setHearts(nextHearts);
    if (nextHearts <= 0) {
      window.setTimeout(
        () =>
          onFinish(false, {
            hearts: 0,
            correct: nextCorrect,
            total,
            results: [...results],
            totalMs: spentMs.current,
          }),
        900
      );
    }
  };

  const advance = () => {
    if (hearts <= 0) return;
    if (qi + 1 >= total) {
      onFinish(true, {
        hearts,
        correct,
        total,
        results: [...results],
        totalMs: spentMs.current,
      });
      return;
    }
    setQi(qi + 1);
    setSelection(null);
    setAnswered(false);
    setBuilt(new Set());
    setBuiltKeys(new Map());
    qStart.current = Date.now();
  };

  // Real and typing keyboards feed build questions the same way taps do.
  // Inversions ("buildinv") care which exact key is lowest, not just which
  // pitch classes are down, so they're tracked separately: a Map of
  // "F4"-style id → midi rather than a pitch-class Set.
  const isBuildInv = q.type === "buildinv";
  const isBuild = q.type === "build" || isBuildInv;
  const toggleBuilt = (name) =>
    setBuilt((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  const toggleBuiltKey = (k) =>
    setBuiltKeys((prev) => {
      const next = new Map(prev);
      const id = `${k.name}${k.octave}`;
      if (next.has(id)) next.delete(id);
      else next.set(id, k.midi);
      return next;
    });
  const onKeyPress = (k) => {
    if (!isBuild || answered) return;
    if (isBuildInv) toggleBuiltKey(k);
    else toggleBuilt(k.name);
  };
  const midi = useMidi(onKeyPress, isBuild);
  useTypingPiano(onKeyPress, isBuild && !answered);

  const heartsMini = useMemo(
    () => (
      <div className="hearts-mini">
        {Array.from({ length: SESSION_HEARTS }, (_, i) => (
          <span key={i} style={{ opacity: i < hearts ? 1 : 0.25 }}>
            <Icon name="heart" />
          </span>
        ))}
      </div>
    ),
    [hearts]
  );

  const drillBar = (
    <div className="lessonbar">
      <button className="close" onClick={onClose}>
        ×
      </button>
      <div className="progress">
        <i style={{ width: `${pct}%` }} />
      </div>
      {timed && (
        <div className="v2-clock mono" aria-label="run time">
          {fmtMs(clock)}
        </div>
      )}
      {practice ? (
        <div className="v2-count mono" aria-label={`question ${qi + 1} of ${total}`}>
          {qi + 1}/{total}
        </div>
      ) : (
        heartsMini
      )}
    </div>
  );

  const verdictFoot = (rightNote, wrongNote) =>
    !answered ? null : (
      <div className={`foot ${lastCorrect ? "right" : "wrong"}`}>
        <div className="verdict">
          <b>{lastCorrect ? "yes!" : "not quite"}</b>
          <small>{lastCorrect ? rightNote : wrongNote}</small>
          {lastCorrect && streakNow >= 3 && (
            <span className="v2-streak-chip mono">{streakNow} in a row</span>
          )}
        </div>
        {q.note && <div className="notes">{q.note}</div>}
        <button className={`btn ${lastCorrect ? "" : "loje"}`} onClick={advance}>
          Continue
        </button>
      </div>
    );

  // ---- build finale: tap the whole scale (or slash chord) on the piano ----
  if (isBuild && !isBuildInv) {
    const want = new Set(q.targetNotes);
    const checkBuild = () => {
      const ok = built.size === want.size && [...want].every((n) => built.has(n));
      grade(ok);
    };
    return (
      <div className="ns-root">
        <div className="ns-app">
          {drillBar}
          <div className="qarea">
            <div className="qkind">{q.chord ? "build the chord" : QKIND.build}</div>
            <div className="qprompt">
              play <b>{q.label}</b>
            </div>
            <div className="qprompt-sub muted">
              {q.chord ? "3 notes" : `all ${q.targetNotes.length} notes`} — any
              octave
            </div>
            <Piano
              pressed={built}
              onPress={(k) => !answered && toggleBuilt(k.name)}
              targetNotes={q.targetNotes}
              result={answered ? (lastCorrect ? "right" : "wrong") : null}
              disabled={answered}
            />
            <div className="ns-built">
              {built.size ? [...built].join(" · ") : "tap the notes (any octave)…"}
            </div>
            {CAN_PLAY && answered && q.chord && (
              <div className="ns-listen">
                <PlayButton chords={[q.playNotes]} countIn={false} label="hear it" />
              </div>
            )}
            {CAN_PLAY && answered && !q.chord && (
              <div className="ns-listen">
                <MidiPlayButton midis={q.playMidis} label="hear the scale" />
              </div>
            )}
            <TypingHint midiStatus={midi.status} />
            <MidiBadge status={midi.status} devices={midi.devices} />
          </div>
          {!answered ? (
            <div className="foot">
              <button
                className="btn"
                disabled={built.size < q.targetNotes.length}
                onClick={checkBuild}
              >
                Check
              </button>
            </div>
          ) : (
            verdictFoot(
              "that's the scale",
              `${q.label} is ${q.displayNotes.join("–")}`
            )
          )}
        </div>
      </div>
    );
  }

  // ---- build the flip: same 3 notes, but which one is lowest is the answer ----
  if (isBuildInv) {
    const entries = [...builtKeys.entries()]; // [id, midi][]
    const targetPcs = new Set(q.playMidis.map((m) => m % 12));
    const checkBuildInv = () => {
      const midis = entries.map(([, m]) => m);
      const pressedPcs = new Set(midis.map((m) => m % 12));
      const samePcs =
        pressedPcs.size === targetPcs.size &&
        [...targetPcs].every((pc) => pressedPcs.has(pc));
      const lowest = Math.min(...midis);
      const ok = entries.length === 3 && samePcs && lowest % 12 === q.bassPc;
      grade(ok);
    };
    const exactKeys = new Set(entries.map(([id]) => id));
    const orderedNames = entries
      .sort((a, b) => a[1] - b[1])
      .map(([id]) => id.replace(/\d+$/, ""));
    return (
      <div className="ns-root">
        <div className="ns-app">
          {drillBar}
          <div className="qarea">
            <div className="qkind">{QKIND.buildinv}</div>
            <div className="qprompt">
              play <b>{q.label}</b>
            </div>
            <div className="qprompt-sub muted">
              {q.invName} — {q.bass} on the bottom · 3 notes, any octave
            </div>
            <Piano
              exactKeys={exactKeys}
              onPress={(k) => !answered && toggleBuiltKey(k)}
              targetNotes={q.targetNotes}
              result={answered ? (lastCorrect ? "right" : "wrong") : null}
              disabled={answered}
            />
            <div className="ns-built">
              {orderedNames.length
                ? orderedNames.join(" · ")
                : "tap the 3 notes — which key ends up lowest is the answer…"}
            </div>
            {CAN_PLAY && answered && (
              <div className="ns-listen">
                <MidiChordButton chords={[q.playMidis]} label="hear it" />
              </div>
            )}
            <TypingHint midiStatus={midi.status} />
            <MidiBadge status={midi.status} devices={midi.devices} />
          </div>
          {!answered ? (
            <div className="foot">
              <button className="btn" disabled={entries.length < 3} onClick={checkBuildInv}>
                Check
              </button>
            </div>
          ) : (
            verdictFoot(
              "that's the flip",
              `${q.label} is ${q.displayNotes.join("–")} — ${q.bass} on the bottom`
            )
          )}
        </div>
      </div>
    );
  }

  // ---- every other question: a choice grid ----
  const renderChoice = (label) => {
    let cls = "choice";
    if (answered) {
      if (label === q.correct) cls += " right";
      else if (label === selection) cls += " wrong";
      else cls += " dim";
    } else if (selection === label) {
      cls += " sel";
    }
    // scale-type names read better as prose; invear's options are prose too
    // ("root position" etc), while invname's slash names/stacks stay mono.
    const mono = q.type !== "ear" && q.type !== "invear";
    return (
      <button
        key={label}
        className={cls}
        style={answered ? { pointerEvents: "none" } : undefined}
        onClick={() => !answered && setSelection(label)}
      >
        <span className={mono ? "mono" : ""}>{label}</span>
      </button>
    );
  };

  return (
    <div className="ns-root">
      <div className="ns-app">
        {drillBar}
        <div className="qarea">
          <div className="qkind">
            {q.type === "nextnote" && q.down ? "coming down" : QKIND[q.type]}
          </div>
          <div className="qprompt">{q.prompt}</div>
          {q.type === "ear" && CAN_PLAY && (
            <div className="ns-listen">
              <MidiPlayButton midis={q.playMidis} big label="play the scale" />
            </div>
          )}
          {q.type === "chordear" && CAN_PLAY && (
            <div className="ns-listen">
              <PlayButton chords={q.playChords} countIn={false} big label="hear 1 → ?" />
            </div>
          )}
          {q.type === "quality" && CAN_PLAY && answered && (
            <div className="ns-listen">
              <PlayButton chords={[q.playNotes]} countIn={false} label="hear it" />
            </div>
          )}
          {q.type === "invear" && CAN_PLAY && (
            <div className="ns-listen">
              <MidiChordButton chords={q.playMidiChords} big label="root → flip" />
            </div>
          )}
          {q.type === "invname" && CAN_PLAY && answered && (
            <div className="ns-listen">
              <MidiChordButton chords={[q.playMidis]} label="hear it" />
            </div>
          )}
          <div className={`choices${q.options.length > 4 ? " many" : ""}`}>
            {q.options.map(renderChoice)}
          </div>
        </div>
        {!answered ? (
          <div className="foot">
            <button
              className="btn"
              disabled={!selection}
              onClick={() => grade(selection === q.correct)}
            >
              Check
            </button>
          </div>
        ) : (
          verdictFoot("that's right", `answer: ${q.correct}`)
        )}
      </div>
    </div>
  );
};

export default DrillScreen;
